import React, { createContext, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { parseAwards } from 'utils/awards';
import { objectToArray } from 'utils/array';
import {
  getRegionTitleByCountry,
  getCountryNameByCode,
  getCountryCodeByName,
} from 'utils/territories';
import { getMovieData } from 'api/film';
import { REACT_APP_API_URL } from 'config';
import CountryData from 'country-data';
import defaultFilmData from './defaultFilmData';
import {
  validateCastItem,
  getCastValidationState,
  getFieldValidation,
  validateDistributorItem,
  getDistributorFieldValidation,
  validateCreditsItem,
  getCreditsFieldValidation,
  validateReleaseDateItem,
} from '../../../validation/filmValidationSchemas';

const initialState = {
  oldData: {},
  data: {},
  setInitialData: () => {},
  update: () => {},
  imageUrl: null,
  setImageUrl: () => {},
  imageForNewFilm: null,
  setImageForNewFilm: () => {},
  fetchFilmData: async () => {},
  isContainNoneEnglish: false,
  setIsContainNonEnglish: () => {},
  isReleased: false,
  setIsReleased: () => {},
  isFestivals: false,
  setIsFestivals: () => {},
  istTheatricalExhibition: false,
  setIsTheatricalExhibition: () => {},
  isBasedOn: false,
  setIsBasedOn: () => {},
  isDevelopmentDeal: false,
  setIsDevelopmentDeal: () => {},
  prepareToSave: () => {},
  validate: () => {},
  prepareDistributorsToSave: () => {},
  prepareReleaseDateToSave: () => {},
  transformMovieDataToInitialData: () => {},
  getDistributorsSplitByRegion: () => {},
  prepareAwardsToSave: () => {},
  prepareFinancialsToSave: () => {},
  validateCastItem: () => {},
  getCastValidationState: () => {},
  getFieldValidation: () => {},
  validateDistributorItem: () => {},
  getDistributorFieldValidation: () => {},
  validateCreditsItem: () => {},
  getCreditsFieldValidation: () => {},
};

const FilmDetailsContext = createContext(initialState);

const FilmDetailsProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [updatedData, setUpdatedData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageForNewFilm, setImageForNewFilm] = useState(null);

  // toggles
  const [isContainNoneEnglish, setIsContainNonEnglish] = useState(false);
  const [isReleased, setIsReleased] = useState(false);
  const [isFestivals, setIsFestivals] = useState(false);
  const [istTheatricalExhibition, setIsTheatricalExhibition] = useState(false);
  const [isBasedOn, setIsBasedOn] = useState(false);
  const [isDevelopmentDeal, setIsDevelopmentDeal] = useState(false);

  const prepareDistributorsToSave = (distributors) => {
    let distributorsToSave = [];

    if (!distributors) return null;

    if (Array.isArray(distributors)) {
      distributorsToSave = [...distributors];
    } else {
      const hasAnyDistributors = Object.values(distributors).some(
        (regionDistributors) =>
          Array.isArray(regionDistributors) && regionDistributors.length > 0,
      );

      if (!hasAnyDistributors) return null;

      Object.keys(distributors).forEach((region) => {
        if (distributors[region] && Array.isArray(distributors[region])) {
          distributors[region].forEach((distributor) => {
            if (!distributor.name || !distributor.name.toString().trim()) {
              return;
            }

            const cleanDistributor = {
              uid: distributor.uid || null,
              name: distributor.name
                ? distributor.name.toString().trim()
                : null,
              country: null,
              terr_minimums: distributor.terr_minimums
                ? distributor.terr_minimums.toString().trim()
                : null,
              contact: distributor.contact
                ? distributor.contact.toString().trim()
                : null,
              format: distributor.format || null,
            };

            if (distributor.country) {
              const countryCode = getCountryCodeByName(distributor.country);
              cleanDistributor.country = countryCode || distributor.country;
            }

            delete cleanDistributor.itemId;

            distributorsToSave.push(cleanDistributor);
          });
        }
      });
    }

    return distributorsToSave.length > 0 ? distributorsToSave : null;
  };

  const prepareAwardsToSave = (awards) => {
    let awardsToSave = [];

    if (!awards) return awardsToSave;

    const prepareAwardToSave = (award) => {
      const awardToSave = { ...award };

      if (awardToSave.recipients) {
        if (
          !Array.isArray(awardToSave.recipients) &&
          typeof awardToSave.recipients === 'string'
        ) {
          awardToSave.recipients = awardToSave.recipients
            .split(',')
            .map((item) => item.trim());
        }
      } else {
        awardToSave.recipients = [];
      }

      return awardToSave;
    };

    if (Array.isArray(awards)) {
      awardsToSave = awards.map(prepareAwardToSave);
    } else {
      awardsToSave = objectToArray(awards).map(prepareAwardToSave);
    }

    return awardsToSave;
  };

  const prepareFinancialsToSave = (financials) => ({
    ...financials,
    domestic_home_ent_margin: financials?.domestic_home_ent_margin?.default
      ? null
      : financials?.domestic_home_ent_margin?.value,
    domestic_tv_streaming: financials?.domestic_tv_streaming?.default
      ? null
      : financials?.domestic_tv_streaming?.value,
  });

  const prepareDataToSave = (data) => {
    if (!data) return data;

    const validationConfig = [
      // Cast validation
      { path: ['cast', 'actors'], fields: ['name', 'type'] },
      { path: ['cast', 'producers'], fields: ['name', 'role'] },
      { path: ['cast', 'directors'], fields: ['name'] },
      { path: ['cast', 'writers'], fields: ['name'] },

      // Direct array validation
      { path: ['distributors'], fields: ['name'] },
      { path: ['release_dates'], fields: ['country', 'date'] },
      {
        path: ['locations'],
        fields: ['name', 'city', 'region_state', 'country'],
      },
      { path: ['production_companies'], fields: ['company'] },
      { path: ['special_effects'], fields: ['company'] },
      { path: ['additional_companies'], fields: ['company', 'role'] },
      { path: ['awards'], fields: ['name', 'year'] },
      { path: ['awards_nominated'], fields: ['name', 'year'] },
    ];

    const filterArray = (items, requiredFields) => {
      if (!Array.isArray(items)) return items;
      return items.filter((item) =>
        requiredFields.every(
          (field) => item[field] && String(item[field]).trim(),
        ),
      );
    };

    const getNestedValue = (obj, path) => {
      return path.reduce((current, key) => current?.[key], obj);
    };

    const setNestedValue = (obj, path, value) => {
      const lastKey = path[path.length - 1];
      const parent = path.slice(0, -1).reduce((current, key) => {
        if (!current[key]) current[key] = {};
        return current[key];
      }, obj);
      parent[lastKey] = value;
    };

    let filteredData = { ...data };

    validationConfig.forEach(({ path, fields }) => {
      const items = getNestedValue(filteredData, path);
      if (Array.isArray(items)) {
        setNestedValue(filteredData, path, filterArray(items, fields));
      }
    });

    return filteredData;
  };

  const prepareReleaseDateToSave = (releaseDate) => {
    const releaseDateToSave = [];

    if (!releaseDate || !releaseDate.length) return releaseDateToSave;

    releaseDate.forEach((item) => {
      if (item.country) {
        const countryCode =
          item.country === 'Worldwide' ? 'WORLDWIDE' : item.country;

        const countryName = getCountryNameByCode(countryCode);

        if (countryName || countryCode === 'WORLDWIDE') {
          releaseDateToSave.push({
            ...item,
            country: countryCode,
          });
        }
      } else {
        releaseDateToSave.push({
          ...item,
        });
      }
    });

    return releaseDateToSave;
  };

  const prepareToSave = useCallback(
    (info, isFile) => ({
      ...info,
      genres: info.genres?.length ? info.genres : null,
      keywords: info.keywords?.length ? info.keywords : null,
      production_countries: info.production_countries?.length
        ? info.production_countries
        : null,
      locations: info.locations?.length ? info.locations : null,
      production_companies: info.production_companies?.length
        ? info.production_companies
        : null,
      special_effects: info.special_effects?.length
        ? info.special_effects
        : null,
      additional_companies: info.additional_companies?.length
        ? info.additional_companies
        : null,
      distributors: info.distributors
        ? prepareDistributorsToSave(info.distributors)
        : null,
      awards_nominated: info.awards_nominated?.length
        ? info.awards_nominated
        : null,
      awards:
        Array.isArray(info.awards) && info.awards.length
          ? info.awards
          : info.awards && Object.keys(info.awards).length
            ? objectToArray(info.awards)
            : null,
      release_dates: info.release_dates?.length ? info.release_dates : null,
      official_pages: info.official_pages?.filter((page) => page.length)?.length
        ? info.official_pages?.filter((page) => page.length)
        : null,
      cast: info.cast
        ? {
            ...info.cast,
            directors: info.cast.directors?.length ? info.cast.directors : null,
            writers: info.cast.writers?.length ? info.cast.writers : null,
            producers: info.cast.producers?.length ? info.cast.producers : null,
            actors: info.cast.actors?.length ? info.cast.actors : null,
            editors: info.cast.editors?.length ? info.cast.editors : null,
            casting_directors: info.cast.casting_directors?.length
              ? info.cast.casting_directors
              : null,
            cinematographers: info.cast.cinematographers?.length
              ? info.cast.cinematographers
              : null,
            composers: info.cast.composers?.length ? info.cast.composers : null,
            production_designers: info.cast.production_designers?.length
              ? info.cast.production_designers
              : null,
            art_directors: info.cast.art_directors?.length
              ? info.cast.art_directors
              : null,
            animators: info.cast.animators?.length ? info.cast.animators : null,
          }
        : null,
      downloadables: info.downloadables
        ? {
            ...info.downloadables,
            film_data: info.downloadables.film_data?.length
              ? info.downloadables.film_data
              : null,
            script: info.downloadables.script?.length
              ? info.downloadables.script
              : null,
            production_budget: info.downloadables.production_budget?.length
              ? info.downloadables.production_budget
              : null,
            marketing_budget: info.downloadables.marketing_budget?.length
              ? info.downloadables.marketing_budget
              : null,
            distribution_agreement: info.downloadables.distribution_agreement
              ?.length
              ? info.downloadables.distribution_agreement
              : null,
            development_deal: info.downloadables.development_deal?.length
              ? info.downloadables.development_deal
              : null,
          }
        : defaultFilmData.downloadables,
      non_english_dialogues:
        (isContainNoneEnglish || isFile) && info.non_english_dialogues?.length
          ? info.non_english_dialogues
          : null,
      financials: {
        ...info.financials,
      },
      festivals:
        (isFestivals || isFile) && info.festivals?.length
          ? info.festivals
          : null,
      theatrical_exhibition:
        (istTheatricalExhibition || isFile) &&
        info.theatrical_exhibition?.length
          ? info.theatrical_exhibition
          : null,
      based_on:
        (isBasedOn || isFile) && info.based_on?.length ? info.based_on : null,
      development_deal:
        (isDevelopmentDeal || isFile) && info.development_deal
          ? info.development_deal
          : null,
      summary: info.summary?.trim() || null,
    }),
    [
      isBasedOn,
      isContainNoneEnglish,
      isDevelopmentDeal,
      isFestivals,
      istTheatricalExhibition,
    ],
  );

  const validate = (info) => {
    const needToBeFilled = ['title'];
    const filled = [];
    const errors = [];

    for (let i = 0; i < needToBeFilled.length; i += 1) {
      const field = needToBeFilled[i];

      switch (field) {
        case 'title':
          if (info[field] && info[field].trim()) {
            filled.push('title');
          } else {
            errors.push('Title is required');
          }
          break;
        case 'directors':
          if (info.cast?.directors && info.cast.directors.length > 0) {
            // Check if all directors have the required 'name' field
            const validDirectors = info.cast.directors.filter(
              (director) => director && director.name && director.name.trim(),
            );
            if (validDirectors.length > 0) {
              filled.push('directors');
            }
          }
          break;
        case 'producers':
          if (info.cast?.producers && info.cast.producers.length > 0) {
            // Check if all producers have the required 'name' and 'role' fields
            const validProducers = info.cast.producers.filter(
              (producer) =>
                producer &&
                producer.name &&
                producer.name.trim() &&
                producer.role &&
                producer.role.trim(),
            );
            if (validProducers.length > 0) {
              filled.push('producers');
            }
          }
          break;
        case 'writers':
          if (info.cast?.writers && info.cast.writers.length > 0) {
            // Check if all writers have the required 'name' field
            const validWriters = info.cast.writers.filter(
              (writer) => writer && writer.name && writer.name.trim(),
            );
            if (validWriters.length > 0) {
              filled.push('writers');
            }
          }
          break;
        case 'actors':
          if (info.cast?.actors && info.cast.actors.length > 0) {
            // Check if all actors have the required 'name' and 'type' fields (character is not required)
            const validActors = info.cast.actors.filter(
              (actor) =>
                actor &&
                actor.name &&
                actor.name.trim() &&
                actor.type &&
                actor.type.trim(),
            );
            if (validActors.length > 0) {
              filled.push('actors');
            }
          }
          break;
        case 'domestic_country':
          if (info[field]) {
            filled.push('domestic_country');
          }
          break;
        case 'domestic_release_date':
          if (info[field]) {
            filled.push('domestic_release_date');
          }
          break;
        case 'genres':
          if (
            info[field] &&
            Array.isArray(info[field]) &&
            info[field].length > 0
          ) {
            filled.push('genres');
          }
          break;
        default:
          if (info[field]) {
            filled.push(field);
          }
          break;
      }
    }

    // Validate release_dates: if any row has at least one value, both must be filled
    if (info.release_dates && Array.isArray(info.release_dates)) {
      const incompleteReleaseDates = info.release_dates.filter(
        (release) => !validateReleaseDateItem(release).isValid,
      );

      if (incompleteReleaseDates.length > 0) {
        errors.push(
          `${incompleteReleaseDates.length} incomplete release ${
            incompleteReleaseDates.length === 1 ? 'date' : 'dates'
          }`,
        );
      }
    }

    // All fields must be filled (not just 3)
    return {
      valid: filled.length === needToBeFilled.length && errors.length === 0,
      needToBeFilled,
      filled,
      errors,
    };
  };

  const setInitialData = useCallback((initialData) => {
    const workingData = initialData || defaultFilmData;
    const {
      non_english_dialogues,
      release_dates,
      financials,
      festivals,
      theatrical_exhibition,
      based_on,
      development_deal,
    } = workingData;

    let awardsToShow = [];

    if (workingData.awards) awardsToShow = [...workingData.awards];

    if (workingData.awards_nominated)
      awardsToShow = [
        ...awardsToShow,
        ...workingData.awards_nominated.map((item) => ({
          ...item,
          status: 'nominated',
        })),
      ];

    const preparedToShow = {
      ...workingData,
      awards: parseAwards(awardsToShow),
    };

    if (release_dates) {
      setIsReleased(
        release_dates.some((rd) => new Date(rd.date).getTime() < Date.now()),
      );
    }

    setIsContainNonEnglish(
      Array.isArray(non_english_dialogues)
        ? non_english_dialogues.length > 0
        : Boolean(non_english_dialogues),
    );

    setData({ ...preparedToShow });
    setUpdatedData({ ...preparedToShow });
    setImageForNewFilm(null);

    setIsFestivals(festivals?.length > 0);
    setIsTheatricalExhibition(theatrical_exhibition?.length > 0);
    setIsBasedOn(based_on?.length > 0);
    setIsDevelopmentDeal(Boolean(development_deal));
  }, []);

  const update = useCallback(
    (newData) => {
      const prevData = updatedData || {};
      setUpdatedData({ ...prevData, ...newData });

      // Sync downloadables to oldData - file uploads save directly to backend
      if (newData.downloadables) {
        setData((prevOldData) => ({
          ...prevOldData,
          downloadables: newData.downloadables,
        }));
      }
    },
    [updatedData],
  );

  const getDistributorsSplitByRegion = (distributors) => {
    const distributorsSplitByRegion = {
      Worldwide: [],
      'North America': [],
      UK: [],
      Europe: [],
      'Far East': [],
      'Latin America': [],
      'Central America': [],
      'Middle East': [],
      'Eastern Europe': [],
    };

    if (distributors.length) {
      distributors.forEach((distributor) => {
        const countryName = getCountryNameByCode(distributor.country);

        if (countryName) {
          const regionTitle = getRegionTitleByCountry(countryName);

          if (regionTitle)
            distributorsSplitByRegion[regionTitle].push({
              ...distributor,
            });
        } else {
          distributorsSplitByRegion.Worldwide.push({
            ...distributor,
          });
        }
      });
    }

    return distributorsSplitByRegion;
  };

  const transformMovieDataToInitialData = (
    movieData,
    tryParseAwards = true,
  ) => {
    const transformedData = { ...movieData };

    if (Array.isArray(movieData.distributors)) {
      const distributorsSplitByRegion = getDistributorsSplitByRegion(
        movieData.distributors,
      );

      transformedData.distributors = distributorsSplitByRegion;
    }

    if (movieData.release_dates?.length) {
      const releaseDateWithCountryName = movieData.release_dates.map(
        (item) => ({
          ...item,
        }),
      );

      transformedData.release_dates = releaseDateWithCountryName;
    }

    if (tryParseAwards) {
      let awardsToParse = [];

      if (transformedData.awards) awardsToParse = [...transformedData.awards];

      if (transformedData.awards_nominated)
        awardsToParse = [
          ...awardsToParse,
          ...transformedData.awards_nominated.map((item) => ({
            ...item,
            status: 'nominated',
          })),
        ];

      transformedData.awards = parseAwards(awardsToParse);
    }

    return transformedData;
  };

  const fetchFilmData = useCallback(
    async (id) => {
      if (id) {
        const res = await getMovieData(id);
        setImageUrl(res?.poster_url);
        const transformedMovieData = transformMovieDataToInitialData(
          res,
          false,
        );

        setInitialData({ ...transformedMovieData });
      } else {
        setImageUrl(null);
        setInitialData(null);
      }
    },
    [setImageUrl, setInitialData],
  );

  const filmDetails = {
    oldData: data,
    data: updatedData,
    setInitialData,
    update,
    imageUrl,
    setImageUrl,
    imageForNewFilm,
    setImageForNewFilm,
    fetchFilmData,
    isContainNoneEnglish,
    setIsContainNonEnglish,
    isReleased,
    isFestivals,
    setIsFestivals,
    istTheatricalExhibition,
    setIsTheatricalExhibition,
    isBasedOn,
    setIsBasedOn,
    isDevelopmentDeal,
    setIsDevelopmentDeal,
    prepareToSave,
    validate,
    prepareDistributorsToSave,
    prepareReleaseDateToSave,
    transformMovieDataToInitialData,
    getDistributorsSplitByRegion,
    prepareAwardsToSave,
    prepareFinancialsToSave,
    prepareDataToSave,
    validateCastItem,
    getCastValidationState,
    getFieldValidation,
    validateDistributorItem,
    getDistributorFieldValidation,
    validateCreditsItem,
    getCreditsFieldValidation,
  };

  return (
    <FilmDetailsContext.Provider value={filmDetails}>
      {children}
    </FilmDetailsContext.Provider>
  );
};

FilmDetailsProvider.propTypes = {
  children: PropTypes.any,
};

const useFilmDetails = () => useContext(FilmDetailsContext);

export { FilmDetailsProvider, useFilmDetails };
